import { useBridgeRestrictions } from '@/api/health';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

type RestrictionChipsProps =
  | { scope: 'global' }
  | { scope: 'account'; partnerTypeCode: string | undefined };

const BOOKING_TOOLTIP =
  'BRIDGE_DISABLE_WRITES is on. Guesty reservation and webhook calls are skipped and logged. Publishing, unpublishing and image removal on ScoutAround are unaffected.';

const CAP_CHIP_CLASSES = 'border-amber-500 bg-amber-50 text-amber-900 dark:bg-amber-950 dark:text-amber-100';

/**
 * One chip per active runtime restriction (see GET /health/status). In the
 * global scope every active restriction shows; on an account page only those
 * that apply to its partner type: booking writes for Guesty, the sync cap for
 * anything that syncs (not file import).
 */
export function RestrictionChips(props: RestrictionChipsProps): JSX.Element | null {
  const { data: restrictions, isLoading, isError } = useBridgeRestrictions();

  if (isLoading || isError || !restrictions) return null;

  const isGlobal = props.scope === 'global';
  const partnerTypeCode = props.scope === 'account' ? props.partnerTypeCode : undefined;

  const showBookingChip =
    restrictions.partnerBookingWrites === 'off' && (isGlobal || partnerTypeCode === 'guesty');
  const cap = restrictions.syncListingCap;
  const showCapChip = cap !== null && (isGlobal || partnerTypeCode !== 'file_import');

  if (!showBookingChip && !showCapChip) return null;

  return (
    <TooltipProvider delayDuration={0}>
      <div data-testid="restriction-chips" className="flex items-center gap-2">
        {showBookingChip && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="secondary" data-testid="restriction-booking-writes" tabIndex={0}>
                Partner booking push off
              </Badge>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              {BOOKING_TOOLTIP}
            </TooltipContent>
          </Tooltip>
        )}
        {showCapChip && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="outline" className={CAP_CHIP_CLASSES} data-testid="restriction-sync-cap" tabIndex={0}>
                {`Sync capped at ${String(cap)} listings per account`}
              </Badge>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              {`BRIDGE_SYNC_LIMIT is set. Each partner sync reads at most ${String(cap)} listings, so removal detection is skipped (sync log reason sync_limit). Set BRIDGE_SYNC_LIMIT=false for full syncs.`}
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
}
